import { useMemo } from 'react';
import moment from 'moment';
import * as LucideIcons from 'lucide-react';
import { File } from '../../../types/files';
import { TableColumn, StatusTag } from '../../shared/table';
import { InitialsAvatar } from '../../shared';

interface UseFilesTableConfigOptions {
  isGroupPage?: boolean;
}

export const useFilesTableConfig = (options: UseFilesTableConfigOptions = {}): TableColumn<File>[] => {
  const { isGroupPage = false } = options;

  return useMemo<TableColumn<File>[]>(() => [
    {
      header: 'File Name / Description',
      width: '280px',
      flexGrow: true,
      renderCell: (file) => {
        const IconComponent = file.type === 'Photo' ? LucideIcons.Image : LucideIcons.FileText;
        
        return (
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
              <IconComponent className="w-5 h-5 text-gray-600" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold truncate">
                {file.filename}
              </span>
              <span className="text-xs text-gray-500 line-clamp-1">
                {file.description}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      header: 'Type',
      width: '100px',
      renderCell: (file) => (
        <StatusTag 
          variant={file.type === 'Photo' ? 'info' : 'default'} 
          label={file.type} 
          dataTarget={`file-type-${file.id}`}
        />
      ),
    },
    {
      header: 'Uploaded By',
      width: '160px',
      renderCell: (file) => (
        <div className="flex items-center gap-2">
          <InitialsAvatar 
            personId={file.uploadedById} 
            name={file.uploadedBy}
            size={24} 
          />
          <span className="text-sm truncate">{file.uploadedBy}</span>
        </div>
      ),
    },
    {
      header: 'Size',
      width: '100px',
      renderCell: (file) => <span className="text-sm text-gray-600">{file.size}</span>,
    },
    {
      header: 'Date',
      width: '120px',
      renderCell: (file) => (
        <span className="text-sm text-gray-600">
          {moment(file.dateUploaded).format('MMM D, YYYY')}
        </span>
      ),
    },
  ], [isGroupPage]);
};
